import { Component, ChangeDetectionStrategy, computed, inject, signal, effect, input, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ToastrService } from 'ngx-toastr';

// Tier 2: removed Wijmo `CollectionView`, `WjGridModule`, `WjGridFilterModule`.
// AG Grid vía KiteGridComponent + manual filter computed.

import { SharedModule } from '../../../../../layout/shared/shared.module';
import { KiteModule } from '../../../../../shared/widgets/kite/kite.module';
import { DateFilterValue } from '../../../../../shared/widgets/document-viewer/components/date-filter/date-filter.component';

import { ResumeDataService } from '../../services/resume-data.service';
import { ReconciliationsService } from '../../../../../services/customers/reconciliations.service';
import { DialogsService } from '../../../../../services/dialogs.service';
import { toDateOnly } from '../../../../../utilities/format-date';
import { WidgetsModule } from '../../../../../shared/widgets.module';
import { ClientsService } from '../../../../../services/customers/clients.service';

// Tier 2 imports: factory column defs.
import {
  createPlainColumn
} from '../../../../../shared/components/kite-grid/column-utils/column-utils';

@Component({
    selector: 'app-oil-collection-resume-tab',
    imports: [CommonModule, SharedModule, KiteModule, WidgetsModule
        // WjGridModule + WjGridFilterModule + CustomPaginatorComponent removed (Tier 2.2).
        // KiteGridComponent (exposed by WidgetsModule) brings its own paginator.
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './oil-collection-resume-tab.component.html'
})
export class OilCollectionResumeTabComponent implements OnInit {

  readonly resumeData = inject(ResumeDataService);
  private readonly reconciliationsService = inject(ReconciliationsService);
  private readonly modalsService = inject(DialogsService);
  private readonly toastr = inject(ToastrService);
  private readonly clientsService = inject(ClientsService);
  private readonly datePipe = inject(DatePipe);

  readonly isVisible = input.required<boolean>();

  // Tier 2: `collections` was `signal<CollectionView<any>>` — replaced with plain array +
  // manual computed filter + slice pagination.
  /** All mapped collections (formatted created_at). Powers the grid. */
  readonly collections = signal<any[]>([]);
  /** Subset after applying the date filter; used for PDF/email and as the
   *  source for pagination. Equivalent to the legacy `data_filtered` signal. */
  readonly data_filtered = signal<any[]>([]);
  readonly loadingReport = signal(false);
  readonly filter_start_date = signal<string | Date | null>('');
  readonly filter_end_date   = signal<string | Date | null>('');
  readonly pageSize = 10;
  readonly currentPage = signal(1);

  /** Paged slice for the wrapper. Resets to 1 when the filter changes. */
  readonly pagedCollections = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize;
    return this.collections().slice(start, start + this.pageSize);
  });

  columnDefs: any[] = [];

  constructor() {
    this.columnDefs = [
      createPlainColumn({
        field: 'created_at',
        headerName: 'Date',
        flex: 1.2,
        cellClass: 'ag-cell-center'
      }),
      createPlainColumn({
        field: 'gallons',
        headerName: 'Total Collected',
        flex: 1,
        cellClass: 'ag-cell-center'
      }),
      createPlainColumn({
        field: 'clean_gallons',
        headerName: 'Oil Clean (no waste)',
        flex: 1,
        cellClass: 'ag-cell-center'
      }),
      createPlainColumn({
        field: 'driver_name',
        headerName: 'Driver',
        width: 250,
        cellClass: 'ag-cell-left'
      })
    ];

    effect(() => {
      const rows = (this.resumeData.oilCollectionTabData()?.collections || []).map((item: any) => {
        return {
          ...item,
          created_at: this.datePipe.transform(item.created_at, 'EEEE, MMMM d, y h:mm a', 'America/New_York')
        };
      });
      if (rows.length > 0 && this.isVisible()) {
        this.collections.set(rows);
        this.data_filtered.set(rows);
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.fetchOilCollectionData();
  }

  fetchOilCollectionData() {
    this.clientsService.getClientCollectionsInformation(this.resumeData.clientID()).subscribe({
      next: (res: any) => {
        this.mapData(res.data);
      },
      error: (err: any) => {
        this.toastr.error(err.error.msg || 'An error occurred while fetching oil collections', 'Error');
      }
    });
  }

  mapData(data: any): void {
    this.resumeData.oilCollectionTab.set(data);
  }

  // ── Date filter ──────────────────────────────────────────────────────────

  onDateFilter(value: any): void {
    if (!value || value.type === 'none') {
      this.clearFilter();
      return;
    }
    this.filter_start_date.set(value.startDate);
    this.filter_end_date.set(value.endDate);
    this.applyDateFilter();
  }

  onDateFilterChange(value: DateFilterValue): void {
    if (!value || value.type === 'none') {
      this.clearFilter();
      return;
    }
    this.filter_start_date.set(value.startDate);
    this.filter_end_date.set(value.endDate);
    this.applyDateFilter();
  }

  private applyDateFilter(): void {
    const startDate = this.filter_start_date();
    const endDate   = this.filter_end_date();

    if (!startDate || !endDate) {
      this.restoreAllData();
      return;
    }

    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end   = endDate   instanceof Date ? endDate   : new Date(endDate);

    if (start > end) {
      this.toastr.warning('Start date cannot be greater than end date', 'Invalid dates');
      return;
    }

    this.setDataRange(start, end);
  }

  private setDataRange(startDate: Date, endDate: Date): void {
    const allRows = this.resumeData.oilCollectionTabData()?.collections || [];
    const filtered = allRows.filter((row: any) => {
      const datex = toDateOnly(row.created_at);
      return datex.getTime() >= startDate.getTime() && datex.getTime() <= endDate.getTime();
    });

    // Tier 2: assign plain array (was: `new CollectionView(...)`).
    this.collections.set(this.mapCollections(filtered));
    this.data_filtered.set(filtered);
    this.currentPage.set(1);
  }

  mapCollections(collections: any[]): any[] {
    return collections.map((item: any) => {
      return {
        ...item,
        created_at: this.datePipe.transform(item.created_at, 'EEEE, MMMM d, y h:mm a')
      };
    });
  }

  clearFilter(): void {
    this.filter_start_date.set(null);
    this.filter_end_date.set(null);
    this.restoreAllData();
  }

  private restoreAllData(): void {
    const rows = this.resumeData.oilCollectionTabData()?.collections || [];
    this.collections.set(this.mapCollections(rows));
    this.data_filtered.set(rows);
    this.currentPage.set(1);
  }

  // ── Actions ─────────────────────────────────────────────────────────────

  exportPDF(): void {
    const startDate = this.filter_start_date();
    const endDate = this.filter_end_date();

    if (!startDate || !endDate) {
      this.modalsService.openFeedbackSuccess({ title: 'alert', msg: 'You must specify a start and end date to generate this report' });
      return;
    }

    this.loadingReport.set(true);
    const clientInfo = this.resumeData.clientInfomationTabData();

    this.reconciliationsService.generateReconciliationCollectionsReport({
      startDate,
      endDate,
      clientId: this.resumeData.clientID()
    }).subscribe({
      next: (res: any) => {
        if (res.ok) {
          const link = document.createElement('a');
          link.href = res.pdf;
          link.download = `UCO-REPORT_${clientInfo?.ident_GUI ?? ''}`;
          link.click();
          link.remove();
          window.URL.revokeObjectURL(res.pdf);
          this.modalsService.openFeedbackSuccess({ title: 'Report', msg: res.msg });
        } else {
          this.modalsService.openFeedbackError({ title: 'Report', msg: res.msg });
        }
        this.loadingReport.set(false);
      },
      error: (err: any) => {
        this.modalsService.openFeedbackError({ title: 'Report', msg: err.error.msg });
        this.loadingReport.set(false);
      }
    });
  }

  sendByEmail(): void {
    const pdfData = this.resumeData.performanceSummaryTab();
    const filteredRows = this.data_filtered();

    if (!pdfData.client_email) {
      this.toastr.warning('The client does not have a registered email.');
      return;
    }

    if (filteredRows.length === 0) {
      this.toastr.warning('No records found.');
      return;
    }

    const rows = filteredRows.map(item => ({
      created_at: item.created_at,
      gallons: item.oil_water,
    }));

    const data = { rows, dataPdf: pdfData };

    fetch('https://kite-pro.com/back-dev/oilcollections/send-email-customer-resume', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
      .then(() => this.toastr.success('Email sent successfully'))
      .catch(() => this.toastr.error('Error sending the email'));
  }

  refreshData(page: number): void {
    // Tier 2: was `this.collections()?.moveToPage(page - 1)`.
    this.currentPage.set(page);
  }
}
